/**
 * Клиент API личного кабинета. Сессия живёт в HttpOnly-cookie, поэтому
 * токен в JS не попадает и не может быть украден через XSS.
 */

export type ApiError = { code: string; message: string; details?: Record<string, unknown> };

export class RequestFailed extends Error {
  constructor(readonly error: ApiError, readonly status: number) {
    super(error.message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
  });

  if (res.status === 401) {
    // Сессия кончилась — уводим на вход, сохранив, куда человек шёл.
    const back = encodeURIComponent(location.pathname + location.search);
    location.href = `/login?redirect=${back}`;
    throw new RequestFailed({ code: 'UNAUTHORIZED', message: 'Нужен вход' }, 401);
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: ApiError } | null;
    throw new RequestFailed(body?.error ?? { code: 'INTERNAL', message: 'Что-то пошло не так' }, res.status);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export type Balance = { pending: number; hold: number; available: number; inDebt: boolean };

export type GradeTier = { fromOrdinal: number; toOrdinal: number | null; bonuses: number };

export type Me = {
  user: {
    id: string;
    displayName: string | null;
    avatarUrl: string | null;
    referralCode: string | null;
    referralUrl: string | null;
  };
  /** false — вошли через ВК, но ученик ещё не найден в базе основной платформы. */
  linked: boolean;
  /** true — аккаунт переехал на канонический id, сессию надо перевыпустить. */
  relogin: boolean;
  balance: Balance;
  summary: {
    pending: { count: number; bonuses: number };
    held: { count: number; bonuses: number };
    available: { count: number; bonuses: number };
    successfulReferees: number;
    invitedCount: number;
    next: { ordinal: number; bonuses: number };
    scale: GradeTier[];
    holdDays: number;
  };
};

export type Accrual = {
  id: string;
  externalOrderId: string;
  refereeExternalId: string;
  orderType: string;
  state: string;
  amount: number;
  ordinal: number | null;
  rejectReason: string | null;
  paidAt: string | null;
  releaseAt: string | null;
  createdAt: string;
};

export type Product = {
  id: string;
  category_slug: string;
  title: string;
  description: string;
  image_url: string | null;
  price_bonuses: number;
  delivery_kind: string;
  stock: number | null;
  available: boolean;
};

export type Category = { id: string; slug: string; title: string; sort_order: number };

export type Purchase = {
  id: string;
  title: string;
  priceBonuses: number;
  state: string;
  code: string | null;
  codeExpiresAt: string | null;
  createdAt: string;
};

export type ContentBlock = {
  key: string;
  kind: 'TEXT' | 'MARKDOWN' | 'HTML' | 'FAQ';
  title: string;
  body: string;
  items: { question: string; answer: string }[];
};

export const api = {
  me: () => request<Me>('/api/me'),
  accruals: (cursor?: string) =>
    request<{ items: Accrual[]; nextCursor: string | null }>(
      `/api/me/accruals?limit=20${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`,
    ),
  purchases: (cursor?: string) =>
    request<{ items: Purchase[]; nextCursor: string | null }>(
      `/api/me/purchases?limit=20${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`,
    ),
  catalog: () => request<{ categories: Category[]; products: Product[] }>('/api/shop/catalog'),
  content: () => request<{ items: ContentBlock[] }>('/api/content'),
  buy: (productId: string, idempotencyKey: string) =>
    request<{ id: string; state: string; title: string; code: string | null; codeExpiresAt: string | null }>(
      '/api/shop/purchase',
      { method: 'POST', body: JSON.stringify({ productId, idempotencyKey }) },
    ),
  logout: () => request<{ ok: boolean }>('/auth/logout', { method: 'POST', body: '{}' }),
};

export const formatBonuses = (n: number) => new Intl.NumberFormat('ru-RU').format(n);

export const formatDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

/** Сколько дней осталось до выхода из холда — то, что ученик реально хочет знать. */
export function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return ms <= 0 ? 0 : Math.ceil(ms / 86_400_000);
}

export const pluralDays = (n: number) => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} день`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} дня`;
  return `${n} дней`;
};
